import { useCallback, useEffect, useState } from "react";
import { CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { PageHeader, EmptyState, ErrorBanner, LoadingRows, StatusPill, SpinnerButton } from "../../components/app/ui";

export default function FollowUpsPage() {
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [completingId, setCompletingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/followups");
      setFollowups(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const complete = async (followupId) => {
    setCompletingId(followupId);
    try {
      await api.post(`/followups/${followupId}/complete`);
      toast.success("Follow-up marked as completed.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCompletingId(null);
    }
  };

  return (
    <div data-testid="followups-page">
      <PageHeader testId="followups-header" title="Follow-ups" sub="Upcoming appointments first — straight from your clinic schedule." />
      <ErrorBanner message={error} onRetry={load} />

      {loading ? (
        <LoadingRows rows={4} />
      ) : followups.length === 0 ? (
        <div className="bg-white border border-slate-200/80 rounded-2xl shadow-[0_1px_3px_rgba(15,23,42,0.05)]">
          <EmptyState testId="followups-empty" title="No follow-ups scheduled" sub="Scheduled follow-ups for your patients will appear here." />
        </div>
      ) : (
        <div className="space-y-3" data-testid="followups-list">
          {followups.map((f) => {
            const date = new Date(f.appointment_date);
            const isPast = date < new Date() && f.status === "scheduled";
            return (
              <div
                key={f.followup_id}
                data-testid={`followup-row-${f.followup_id}`}
                className="bg-white border border-slate-200/80 rounded-2xl shadow-[0_1px_3px_rgba(15,23,42,0.05)] px-6 py-5 flex flex-wrap items-center gap-4 transition-[border-color,box-shadow] duration-300 hover:border-[#2563EB]/25 hover:shadow-[0_10px_26px_rgba(37,99,235,0.08)]"
              >
                <div className="w-12 h-12 rounded-2xl bg-slate-50 border border-slate-200/80 flex flex-col items-center justify-center shrink-0">
                  <span className="text-sm font-bold text-[#0F172A] leading-none">{date.getDate()}</span>
                  <span className="text-[9px] tracking-[0.15em] uppercase text-slate-400 mt-1">
                    {date.toLocaleString("en", { month: "short" })}
                  </span>
                </div>
                <div className="flex-1 min-w-[180px]">
                  <p className="text-base font-medium text-[#0F172A]">
                    {f.patient ? `${f.patient.first_name} ${f.patient.last_name}` : "Unknown patient"}
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {f.patient?.display_id} · {date.toLocaleDateString()} {date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </p>
                  {f.notes && <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1.5"><CalendarCheck className="w-3.5 h-3.5 text-[#0891B2]" strokeWidth={1.7} />{f.notes}</p>}
                </div>
                {isPast && <span className="text-[10px] font-semibold tracking-[0.12em] uppercase text-amber-600">Overdue</span>}
                <StatusPill status={f.status} />
                {f.status === "scheduled" && (
                  <SpinnerButton
                    loading={completingId === f.followup_id}
                    onClick={() => complete(f.followup_id)}
                    data-testid={`followup-complete-${f.followup_id}`}
                    className="rounded-full border border-slate-200 px-5 py-2.5 text-xs font-semibold text-slate-500 hover:text-[#1D4ED8] hover:border-[#2563EB]/30 transition-colors duration-200"
                  >
                    Mark done
                  </SpinnerButton>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
